import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import { toast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import Button from "@/v2-backoffice-client/components/button"
import { DotText } from "@/v2-backoffice-client/components/dot"
import LicenseProductSelect from "@/v2-backoffice-client/license/LicenseProductSelect"
import LicenseSeatSourceSelect from "@/v2-backoffice-client/license/plan/LicenseSeatSourceSelect"
import {
  SEAT_SOURCE_COMPANION,
  SEAT_SOURCE_FLAT,
  SEAT_SOURCE_QUANTITY,
} from "@/v2-backoffice-client/license/license.constants"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"
import {
  deriveSeatModel,
  describeSeatModel,
  formatListPrice,
  formatSeatLinePrice,
  licenseWord,
  SEAT_MODEL_KIND,
  type SeatModel,
  type SeatModelPlan,
} from "@/v2-backoffice-client/license/plan/license.plan.seatModel.utils"
import { useLicensePlanUpdateMutation } from "@/v2-backoffice-client/license/plan/update/license.plan.update.mutation"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

// Radix Select items can't carry an empty-string value, so "not set" gets a
// sentinel that is translated to null on submit.
const NOT_SET_VALUE = "0"

export type LicensePlanUpdateDialogProps = {
  plan: LicensePlanFragment
  open: boolean
  onOpenChange(open: boolean): void
}

// Update License: the things we own on a Fenerum-mirrored license — which
// product it is for, its seat model (how a subscription's quantity becomes
// seats), how many seats one unit of it is, and for a fixed-price license
// which per-seat license its extra seats are sold on. Everything else on
// the row is Fenerum's and rewritten by the sync.
//
// Written for a short attention span: every field has one line under it,
// the explanations sit in the dropdown items where the choice is made, and
// the preview at the bottom is the board's own pill plus one sentence — the
// same rule the board and the panel follow (license.plan.seatModel.utils),
// so what the operator sees here is what the board will show. Triggered
// from the board's row menu and the license panel's toolbar.
function LicensePlanUpdateDialog(props: LicensePlanUpdateDialogProps) {
  const { plan, open, onOpenChange } = props
  const [productCode, setProductCode] = useState(
    plan.ProductCode !== null && plan.ProductCode !== undefined
      ? String(plan.ProductCode)
      : NOT_SET_VALUE,
  )
  const [seatSourceCode, setSeatSourceCode] = useState(
    plan.SeatSourceCode !== null && plan.SeatSourceCode !== undefined
      ? String(plan.SeatSourceCode)
      : NOT_SET_VALUE,
  )
  const [includedSeats, setIncludedSeats] = useState(
    plan.IncludedSeats != null ? String(plan.IncludedSeats) : "",
  )
  const [seatLinePlanId, setSeatLinePlanId] = useState(
    plan.SeatLinePlanId != null ? String(plan.SeatLinePlanId) : NOT_SET_VALUE,
  )

  // The catalog the board already holds (Apollo cache): the seat line of a
  // base license is another row of it.
  const { data: plansData } = useLicensePlansQuery()
  const plans = plansData?.getLicensePlans ?? []

  const isPerSeat = seatSourceCode === String(SEAT_SOURCE_QUANTITY)
  const isFixedPrice = seatSourceCode === String(SEAT_SOURCE_COMPANION)
  const isCompanyWide = seatSourceCode === String(SEAT_SOURCE_FLAT)
  const hasSeatsField = isPerSeat || isFixedPrice
  const hasSeatLine = isFixedPrice || isCompanyWide
  const parsedIncludedSeats =
    includedSeats === "" ? null : Number(includedSeats)
  // A per-seat unit is at least one seat (empty reads as 1); a fixed price
  // may include none.
  const isIncludedSeatsValid =
    parsedIncludedSeats === null ||
    (Number.isInteger(parsedIncludedSeats) &&
      parsedIncludedSeats >= (isPerSeat ? 1 : 0))

  // What a fixed-price license of the chosen product can name as its seat
  // line: the product's per-seat licenses, the one it names today included
  // even when deprecated (so the choice is visible, and can be moved off).
  const selectedProductCode =
    productCode === NOT_SET_VALUE ? null : Number(productCode)
  const seatLineOptions = plans
    .filter(
      (candidate) =>
        candidate.Id !== plan.Id &&
        candidate.ProductCode != null &&
        candidate.ProductCode === selectedProductCode &&
        candidate.SeatSourceCode === SEAT_SOURCE_QUANTITY &&
        (!candidate.DeprecatedAt || candidate.Id === plan.SeatLinePlanId),
    )
    .sort((a, b) => a.Name.localeCompare(b.Name))
  // A seat line of another product (the product was just changed) is not a
  // choice any more: it reads, and submits, as none.
  const effectiveSeatLinePlanId =
    hasSeatLine &&
    seatLineOptions.some((option) => String(option.Id) === seatLinePlanId)
      ? Number(seatLinePlanId)
      : null

  // What the choice means, read through the same rule as the board.
  const preview = deriveSeatModel(
    {
      ...plan,
      ProductCode: selectedProductCode,
      SeatSourceCode:
        seatSourceCode === NOT_SET_VALUE ? null : Number(seatSourceCode),
      IncludedSeats:
        hasSeatsField && isIncludedSeatsValid ? parsedIncludedSeats : null,
      SeatLinePlanId: effectiveSeatLinePlanId,
      SeatLinePlanName:
        seatLineOptions.find((option) => option.Id === effectiveSeatLinePlanId)
          ?.Name ?? null,
    },
    plans,
  )
  const listPrice = formatListPrice(plan)
  const productName =
    plans.find(
      (candidate) =>
        candidate.ProductCode != null &&
        candidate.ProductCode === selectedProductCode,
    )?.ProductName ?? null

  // The mutation returns the LicensePlanFragment, so Apollo updates the
  // normalized row in place — no evict needed.
  const [updatePlan, { loading, error }] = useLicensePlanUpdateMutation({
    onCompleted(data) {
      const updated = data.updateLicensePlan
      toast({
        variant: "success",
        title: "License updated",
        description: updated.ProductName
          ? `${updated.Name} is now for ${updated.ProductName}. Customers on it recount on the next sync, within the hour.`
          : `${updated.Name} has no product yet, so the import skips it.`,
      })
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update License — {plan.Name}</Dialog.Title>
          <Dialog.Description>
            Fenerum owns the price{listPrice ? ` (${listPrice})` : ""} and the
            subscriptions. These fields are ours.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Product</Label>
            <LicenseProductSelect
              value={productCode}
              onValueChange={setProductCode}
            >
              <Select.Item
                value={NOT_SET_VALUE}
                description="The import skips this license's subscriptions: no customer is put on it, no customers or MRR show for it."
              >
                No product
              </Select.Item>
            </LicenseProductSelect>
            <p className="text-xs text-muted-foreground">
              Decides which features the license unlocks, and which per-seat
              licenses can be its seat line.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Seat model</Label>
            <LicenseSeatSourceSelect
              value={seatSourceCode}
              onValueChange={setSeatSourceCode}
            >
              {/* Only while nothing is set yet, and never pickable: there is
                  no reason to un-set a seat model, only to pick one. */}
              {plan.SeatSourceCode == null && (
                <Select.Item
                  value={NOT_SET_VALUE}
                  disabled
                  description="The license works; its customers just get no seat cap until this is set."
                >
                  Not set
                </Select.Item>
              )}
            </LicenseSeatSourceSelect>
            {seatSourceCode === NOT_SET_VALUE ? (
              <DotText variant="destructive" className="text-xs">
                Not set: the license works, customers just get no seat cap.
              </DotText>
            ) : (
              <p className="text-xs text-muted-foreground">
                How a subscription&apos;s quantity becomes seats. The seat count
                is a cap the Seats badge is held against, never enforced.
              </p>
            )}
          </div>

          {hasSeatsField && (
            <div className="space-y-1.5">
              <Label htmlFor="licensePlanIncludedSeats">
                {isPerSeat ? "Seats per unit" : "Seats included"}
              </Label>
              <Input
                id="licensePlanIncludedSeats"
                type="number"
                min={isPerSeat ? 1 : 0}
                placeholder={isPerSeat ? "1" : "0"}
                value={includedSeats}
                onChange={(e) => {
                  setIncludedSeats(e.target.value)
                }}
              />
              <p className="text-xs text-muted-foreground">
                {isPerSeat
                  ? "Usually 1: one unit of the quantity is one seat. A five-seat pack is 5."
                  : "Seats the price covers. 1 when the price is for one user; 0 when the seat line counts every user; the bundle size for a seat bundle."}
              </p>
            </div>
          )}

          {hasSeatLine && (
            <div className="space-y-1.5">
              <Label>Seat line</Label>
              <Select
                value={
                  effectiveSeatLinePlanId != null
                    ? String(effectiveSeatLinePlanId)
                    : NOT_SET_VALUE
                }
                onValueChange={setSeatLinePlanId}
              >
                <Select.Trigger size="md" className="w-full">
                  <Select.Value placeholder="Where are extra seats sold?" />
                </Select.Trigger>
                <Select.Content className="max-w-[440px]">
                  <Select.Item
                    value={NOT_SET_VALUE}
                    description={
                      isFixedPrice
                        ? "A seat bundle: the included seats are the cap, and a bigger team moves to the next size."
                        : "No per-seat license goes with this one."
                    }
                  >
                    {isFixedPrice ? "None — a seat bundle" : "None"}
                  </Select.Item>
                  {seatLineOptions.map((option) => (
                    <Select.Item
                      key={option.Id}
                      value={String(option.Id)}
                      description={seatLineOptionDescription(option)}
                    >
                      {option.Name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select>
              <p className="text-xs text-muted-foreground">
                {isFixedPrice
                  ? "The per-seat license extra seats are sold on; a customer's quantity on it is their extra seats."
                  : "A per-seat license whose seats still count toward the cap beside this one, if any."}
                {selectedProductCode != null &&
                  seatLineOptions.length === 0 &&
                  ` ${productName ?? "This product"} has no per-seat license yet.`}
              </p>
            </div>
          )}

          <LicensePlanPreview preview={preview} productName={productName} />
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={hasSeatsField && !isIncludedSeatsValid}
            data-feature-code="UI_BackofficeLicensePlan_Mapping_Save"
            onClick={() => {
              updatePlan({
                variables: {
                  id: plan.Id,
                  input: {
                    ProductCode: selectedProductCode,
                    SeatSourceCode:
                      seatSourceCode === NOT_SET_VALUE
                        ? null
                        : Number(seatSourceCode),
                    IncludedSeats: hasSeatsField ? parsedIncludedSeats : null,
                    SeatLinePlanId: effectiveSeatLinePlanId,
                  },
                },
              })
            }}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

/** "1 000 NOK/seat/month", "deprecated" — the seat line option as it reads in the list. */
function seatLineOptionDescription(option: SeatModelPlan) {
  const parts = [
    formatSeatLinePrice(option),
    option.DeprecatedAt ? "deprecated" : null,
  ].filter((part): part is string => !!part)
  return parts.length > 0 ? parts.join(" · ") : undefined
}

type LicensePlanPreviewProps = {
  preview: SeatModel
  productName: string | null
}

/** Names up to five licenses, then "and N more". */
function joinNames(names: string[]) {
  const shown = names.slice(0, 5)
  const rest = names.length - shown.length
  const joined =
    shown.length <= 1
      ? (shown[0] ?? "")
      : `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`
  return rest > 0 ? `${joined} and ${rest} more` : joined
}

// The preview: the pill the board will show for this choice, its detail,
// and one sentence about what it connects to — the seat line a base sells
// its seats on, the sizes a bundle sits among, the bases a seat line serves.
// Warnings only when the choice would leave customers without seats.
function LicensePlanPreview(props: LicensePlanPreviewProps) {
  const { preview, productName } = props
  const product = productName ?? "this product"
  const { label, detail } = describeSeatModel(preview)
  const isUnset = preview.kind === SEAT_MODEL_KIND.Undeclared

  return (
    <div
      className="flex flex-col gap-1.5 rounded-md bg-muted/40 px-3 py-2 text-xs"
      data-feature-code="UI_BackofficeLicensePlan_Mapping_Preview"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium">On the board:</span>
        {isUnset ? (
          <DotText variant="destructive">{label}</DotText>
        ) : (
          <Badge size="sm">{label}</Badge>
        )}
        {detail && <span className="text-muted-foreground">{detail}</span>}
      </div>

      <LicensePlanPreviewConnection preview={preview} product={product} />
    </div>
  )
}

function LicensePlanPreviewConnection(props: {
  preview: SeatModel
  product: string
}) {
  const { preview, product } = props
  const seatLine = preview.seatLine

  switch (preview.kind) {
    case SEAT_MODEL_KIND.Companion:
      return seatLine ? (
        <p>
          Extra seats go on <span className="font-medium">{seatLine.Name}</span>
          {preview.basePlans.length > 0
            ? `, shared with ${preview.basePlans.length} other ${product} ${licenseWord(preview.basePlans.length)}`
            : ""}
          .
        </p>
      ) : (
        <DotText variant="warning">
          The seat line it names is no longer in the catalog; pick another.
        </DotText>
      )
    case SEAT_MODEL_KIND.Bundle:
      return (
        <>
          {preview.bundles.length > 1 ? (
            <p>
              {product} sizes:{" "}
              {joinNames(
                preview.bundles.map(
                  (bundle) => `${bundle.Name} (${bundle.IncludedSeats ?? 0})`,
                ),
              )}
              .
            </p>
          ) : (
            <p>The only {product} seat bundle so far.</p>
          )}
          {preview.includedSeats <= 1 && preview.seatLines.length > 0 && (
            <DotText variant="warning">
              {preview.includedSeats === 0
                ? "No seats included and no seat line: customers get no seats."
                : "One seat and no seat line: a bundle usually has more."}{" "}
              Selling extra seats per user? Pick{" "}
              {joinNames(preview.seatLines.map((candidate) => candidate.Name))}{" "}
              as the seat line.
            </DotText>
          )}
        </>
      )
    case SEAT_MODEL_KIND.SeatLine:
      return (
        <p>
          The seat line of {preview.basePlans.length} {product}{" "}
          {licenseWord(preview.basePlans.length)}:{" "}
          {joinNames(preview.basePlans.map((basePlan) => basePlan.Name))}.
        </p>
      )
    case SEAT_MODEL_KIND.Quantity:
      return (
        <p className="text-muted-foreground">
          No {product} license sells its extra seats here yet; a base names its
          seat line in its own Update License.
        </p>
      )
    case SEAT_MODEL_KIND.Flat:
      return seatLine ? (
        <p>
          Seats a customer buys on{" "}
          <span className="font-medium">{seatLine.Name}</span> still count
          toward their cap.
        </p>
      ) : null
    default:
      return null
  }
}

export default LicensePlanUpdateDialog
